import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    CircularProgress,
    useTheme,
    Button,
    Chip,
    Divider,
    Avatar,
    LinearProgress,
    IconButton,
    Menu,
    MenuItem,
    Alert
} from '@mui/material';
import {
    // Orders icons
    Receipt as ReceiptIcon,
    AttachMoney as MoneyIcon,
    CheckCircle as CheckCircleIcon,
    PendingActions as PendingIcon,
    Cancel as CancelIcon,
    TrendingUp as TrendingUpIcon,
    // Stock icons
    Inventory as StockIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Assessment as AssessmentIcon,
    // Inventory icons
    Inventory2 as InventoryIcon,
    AddShoppingCart as AddCartIcon,
    // Counter icons
    Storefront as CounterIcon,
    PowerSettingsNew as PowerIcon,
    // History icons
    History as HistoryIcon,
    TrendingUp as TrendUpIcon,
    TrendingDown as TrendDownIcon,
    // General icons
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    GetApp as ExportIcon,
    Info as InfoIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    Timeline as TimelineIcon,
    LocalOffer as OfferIcon,
    Notifications as AlertIcon,
    // Dashboard specific
    Dashboard as DashboardIcon,
    Restaurant as RestaurantIcon,
    Hotel as HotelIcon,
    LocalBar as BarIcon,
    Coffee as CoffeeIcon,
    Fastfood as FastfoodIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import { APIClubDashboardStats } from '../../../services/APIs';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { formatNumberWithComma2, getUserDetails } from '../../../utils/helpers';
import { fetchBranchs } from '../../../utils/namesListUtils';
import { formatNumberWithComma } from '../../../utils/helpers';

const ClubDashboard = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const showToast = useCustomToast();
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);
    const [branches, setBranches] = useState([]);
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
        fetchBranches();
    }, []);

    useEffect(() => {
        fetchDashboardStats();
    }, [selectedBranch]);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    const fetchBranches = async () => {
        try {
            const data = await fetchBranchs();
            setBranches(data || []);
        } catch (error) {
            console.error('Error fetching branches:', error);
        }
    };

    const fetchDashboardStats = async () => {
        setLoading(true);
        setError(null);

        try {
            let branchIdToUse = selectedBranch;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const response = await axios.get(APIClubDashboardStats, { params });
            
            if (response.data.status === 'success') {
                setStats(response.data.data);
                showToast("Success", "Dashboard data loaded successfully", "success");
            } else {
                throw new Error(response.data.message || 'Failed to load dashboard data');
            }
        } catch (error) {
            let errorMessage = "Failed to load dashboard data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = "Network error. Please check your connection.";
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const getSelectedBranchName = () => {
        if (!selectedBranch) return "All Branches";
        const branch = branches.find(b => b.id === selectedBranch);
        return branch ? `${branch.name} (${branch.code})` : "Selected Branch";
    };

    // Loading State
    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '80vh',
                flexDirection: 'column',
                gap: 3
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="primary">
                    Loading Club Dashboard...
                </Typography>
            </Box>
        );
    }

    // Error State
    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert 
                    severity="error" 
                    action={
                        <Button color="inherit" size="small" onClick={fetchDashboardStats}>
                            Retry
                        </Button>
                    }
                >
                    <Typography variant="h6">Failed to load dashboard</Typography>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            </Container>
        );
    }

    // If no stats loaded
    if (!stats) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="info">
                    <Typography variant="h6">No data available</Typography>
                    <Typography variant="body2">Dashboard statistics are not available at the moment.</Typography>
                </Alert>
            </Container>
        );
    }

    // Render Dashboard
    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DashboardIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                        <Box>
                            <Typography  sx={{ 
                                fontWeight: 'bold',
                                color: theme.palette.primary.main
                            }}>
                                Club Management Dashboard
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Real-time overview of club operations
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <Chip
                                label={getSelectedBranchName()}
                                color="primary"
                                variant="outlined"
                                onClick={() => setBranchSelectionOpen(true)}
                                icon={<FilterIcon />}
                                clickable
                            />
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchDashboardStats}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Branch info */}
                {selectedBranch && (
                    <Box sx={{ mt: 2 }}>
                        <Chip
                            label={`Viewing: ${getSelectedBranchName()}`}
                            color="primary"
                            onDelete={() => setSelectedBranch('')}
                            deleteIcon={<FilterIcon />}
                        />
                    </Box>
                )}
            </Box>

            {/* Club Orders Stats Section */}
            <Typography  sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RestaurantIcon color="primary" />
                    Club Orders Overview
                </Box>
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {/* Total Orders */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Total Orders
                                    </Typography>
                                    <Typography  sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma2(stats.club_orders?.total_orders || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="success.main">
                                        +{formatNumberWithComma2(stats.club_orders?.today_orders || 0)} today
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                                    <ReceiptIcon />
                                </Avatar>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={stats.club_orders?.completed_orders ? 
                                    (stats.club_orders.completed_orders / stats.club_orders.total_orders * 100) : 0
                                } 
                                sx={{ mt: 2 }}
                                color="primary"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Revenue */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'success.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Total Revenue
                                    </Typography>
                                    <Typography  sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma2(stats.club_orders?.total_revenue || 0)}
                                    </Typography>
                                    <Typography variant="body2" color="success.main">
                                        {formatNumberWithComma2(stats.club_orders?.today_revenue || 0)} today
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                                    <MoneyIcon />
                                </Avatar>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <Chip 
                                    label={`${formatNumberWithComma2(stats.club_orders?.completed_orders || 0)} Completed`}
                                    size="small"
                                    color="success"
                                    icon={<CheckCircleIcon />}
                                />
                                <Chip 
                                    label={`${formatNumberWithComma2(stats.club_orders?.pending_orders || 0)} Pending`}
                                    size="small"
                                    color="warning"
                                    icon={<PendingIcon />}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order Status Distribution */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'info.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Order Status
                            </Typography>
                            <Box sx={{ spaceY: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                        <Typography variant="body2">Completed</Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatNumberWithComma2(stats.club_orders?.completed_orders || 0)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PendingIcon color="warning" fontSize="small" />
                                        <Typography variant="body2">Pending</Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatNumberWithComma2(stats.club_orders?.pending_orders || 0)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CancelIcon color="error" fontSize="small" />
                                        <Typography variant="body2">Cancelled</Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatNumberWithComma2(stats.club_orders?.cancelled_orders || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Average Order Value */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'warning.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Avg Order Value
                                    </Typography>
                                    <Typography  sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma2(stats.club_orders?.average_order_value || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        per transaction
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                                    <AssessmentIcon />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Club Stocks Stats Section */}
            <Typography  sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StockIcon color="primary" />
                    Stock Management
                </Box>
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {/* Total Stock Items */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Total Items
                                    </Typography>
                                    <Typography  sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma2(stats.club_stocks?.total_items || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="success.main">
                                        {formatNumberWithComma2(stats.club_stocks?.active_stock || 0)} Active
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                                    <StockIcon />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Stock Value */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'success.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Total Stock Value
                                    </Typography>
                                    <Typography  sx={{ fontWeight: 'bold' }}>
                                        {formatNumberWithComma2(stats.club_stocks?.total_value || 0)}
                                    </Typography>
                                    <Typography variant="caption" color="success.main">
                                        {formatNumberWithComma2(stats.club_stocks?.potential_profit || 0)} Profit Margin
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                                    <MoneyIcon />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Stock Balance */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'info.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Current Stock Balance
                            </Typography>
                            <Typography  sx={{ fontWeight: 'bold', mb: 2 }}>
                                {formatNumberWithComma2(stats.club_stocks?.total_balance || 0)}
                            </Typography>
                            <Box sx={{ spaceY: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Available Units</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatNumberWithComma2(stats.club_stocks?.total_balance || 0)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Selling Value</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                        {formatNumberWithComma(stats.club_stocks?.total_selling_value || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Stock Alerts */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 3,
                        borderLeft: 4,
                        borderColor: 'warning.main',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Stock Alerts
                            </Typography>
                            <Box sx={{ spaceY: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main', width: 32, height: 32 }}>
                                        <WarningIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2">Low Stock</Typography>
                                        <Typography variant="h6" color="warning.main">
                                            {formatNumberWithComma2(stats.club_stocks?.low_stock || 0)} items
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: 'error.light', color: 'error.main', width: 32, height: 32 }}>
                                        <ErrorIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2">Out of Stock</Typography>
                                        <Typography variant="h6" color="error.main">
                                            {formatNumberWithComma2(stats.club_stocks?.out_of_stock || 0)} items
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Inventory & Counters Row */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {/* Club Inventory Stats */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <InventoryIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Inventory Transactions
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                                        <Typography variant="body2" color="primary.contrastText">
                                            Total Transactions
                                        </Typography>
                                        <Typography  color="primary.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.club_inventory?.total_transactions || 0)}
                                        </Typography>
                                        <Typography variant="caption" color="primary.contrastText" sx={{ opacity: 0.8 }}>
                                            {formatNumberWithComma2(stats.club_inventory?.today_transactions || 0)} today
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                                        <Typography variant="body2" color="success.contrastText">
                                            Total Quantity
                                        </Typography>
                                        <Typography  color="success.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.club_inventory?.total_quantity || 0)}
                                        </Typography>
                                        <Typography variant="caption" color="success.contrastText" sx={{ opacity: 0.8 }}>
                                            {formatNumberWithComma2(stats.club_inventory?.today_quantity || 0)} today
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" color="info.contrastText">
                                                    Total Cost Value
                                                </Typography>
                                                <Typography  color="info.contrastText" sx={{ fontWeight: 'bold' }}>
                                                    {formatNumberWithComma2(stats.club_inventory?.total_cost || 0)}
                                                </Typography>
                                            </Box>
                                            <AddCartIcon sx={{ color: 'info.contrastText', fontSize: 40 }} />
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Counters Stats */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <CounterIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Counters Management
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CounterIcon sx={{ color: 'success.contrastText' }} />
                                            <Typography variant="body2" color="success.contrastText">
                                                Total Counters
                                            </Typography>
                                        </Box>
                                        <Typography  color="success.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.counters?.total_counters || 0)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PowerIcon sx={{ color: 'success.contrastText' }} />
                                            <Typography variant="body2" color="success.contrastText">
                                                Active Counters
                                            </Typography>
                                        </Box>
                                        <Typography  color="success.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.counters?.active_counters || 0)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" color="warning.contrastText">
                                                    Counter Utilization
                                                </Typography>
                                                <Typography  color="warning.contrastText" sx={{ fontWeight: 'bold' }}>
                                                    {stats.counters?.utilization_rate?.toFixed(1) || 0}%
                                                </Typography>
                                                <Typography variant="caption" color="warning.contrastText" sx={{ opacity: 0.8 }}>
                                                    Active / Total Counters
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <CircularProgress 
                                                    variant="determinate" 
                                                    value={stats.counters?.utilization_rate || 0} 
                                                    size={60}
                                                    thickness={4}
                                                    sx={{ color: 'warning.contrastText' }}
                                                />
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Stock History & Alerts Row */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {/* Stock History Stats */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <HistoryIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Stock Adjustment History
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Avatar sx={{ 
                                            bgcolor: 'primary.main', 
                                            color: 'white',
                                            width: 60,
                                            height: 60,
                                            margin: '0 auto',
                                            mb: 1
                                        }}>
                                            <TrendUpIcon />
                                        </Avatar>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Adjustments
                                        </Typography>
                                        <Typography  sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.club_stock_history?.total_adjustments || 0)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Avatar sx={{ 
                                            bgcolor: 'success.main', 
                                            color: 'white',
                                            width: 60,
                                            height: 60,
                                            margin: '0 auto',
                                            mb: 1
                                        }}>
                                            <ArrowUpIcon />
                                        </Avatar>
                                        <Typography variant="body2" color="text.secondary">
                                            Positive Adjustments
                                        </Typography>
                                        <Typography  sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            +{formatNumberWithComma2(stats.club_stock_history?.positive_adjustments || 0)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Avatar sx={{ 
                                            bgcolor: 'error.main', 
                                            color: 'white',
                                            width: 60,
                                            height: 60,
                                            margin: '0 auto',
                                            mb: 1
                                        }}>
                                            <ArrowDownIcon />
                                        </Avatar>
                                        <Typography variant="body2" color="text.secondary">
                                            Negative Adjustments
                                        </Typography>
                                        <Typography  sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                            {formatNumberWithComma2(stats.club_stock_history?.negative_adjustments || 0)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" color="info.contrastText">
                                                    Net Quantity Change
                                                </Typography>
                                                <Typography  color="info.contrastText" sx={{ fontWeight: 'bold' }}>
                                                    {stats.club_stock_history?.total_quantity_change >= 0 ? '+' : ''}
                                                    {formatNumberWithComma2(stats.club_stock_history?.total_quantity_change || 0)}
                                                </Typography>
                                            </Box>
                                            <TimelineIcon sx={{ color: 'info.contrastText', fontSize: 40 }} />
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Today's Orders & Alerts */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <AlertIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Today's Quick Stats
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                                        <Typography variant="body2" color="primary.contrastText">
                                            Today's Orders
                                        </Typography>
                                        <Typography  color="primary.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.club_orders?.today_orders || 0)}
                                        </Typography>
                                        <Typography variant="caption" color="primary.contrastText" sx={{ opacity: 0.8 }}>
                                            {formatNumberWithComma2(stats.club_orders?.today_revenue || 0)} revenue
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                                        <Typography variant="body2" color="warning.contrastText">
                                            Today's Adjustments
                                        </Typography>
                                        <Typography  color="warning.contrastText" sx={{ fontWeight: 'bold' }}>
                                            {formatNumberWithComma2(stats.club_stock_history?.today_adjustments || 0)}
                                        </Typography>
                                        <Typography variant="caption" color="warning.contrastText" sx={{ opacity: 0.8 }}>
                                            stock changes
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, mt: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="error.contrastText" sx={{ mb: 1 }}>
                                                    Critical Alerts
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 3 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="error.contrastText" sx={{ opacity: 0.8 }}>
                                                            Low Stock
                                                        </Typography>
                                                        <Typography variant="h6" color="error.contrastText">
                                                            {formatNumberWithComma2(stats.stock_alerts?.low_stock_alerts || 0)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="error.contrastText" sx={{ opacity: 0.8 }}>
                                                            Out of Stock
                                                        </Typography>
                                                        <Typography variant="h6" color="error.contrastText">
                                                            {formatNumberWithComma2(stats.stock_alerts?.out_of_stock_alerts || 0)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <WarningIcon sx={{ color: 'error.contrastText', fontSize: 40 }} />
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top Selling Items */}
            {stats.top_selling_items && stats.top_selling_items.length > 0 && (
                <Card elevation={3} sx={{ borderRadius: 3, mb: 5 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <TrendingUpIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Top Selling Items
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            {stats.top_selling_items.slice(0, 4).map((item, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Avatar sx={{ 
                                                bgcolor: index === 0 ? 'gold' : 
                                                         index === 1 ? 'silver' : 
                                                         index === 2 ? '#CD7F32' : 'primary.light',
                                                color: index < 3 ? 'white' : 'primary.main'
                                            }}>
                                                {index + 1}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.uom}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ spaceY: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Quantity Sold:</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatNumberWithComma2(item.total_quantity)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Revenue:</Typography>
                                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                                    {formatNumberWithComma2(item.total_revenue || 0)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Summary Footer */}
            <Card elevation={3} sx={{ borderRadius: 3, bgcolor: 'primary.dark', color: 'white' }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Club Operations Summary
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                • Total Orders: {formatNumberWithComma2(stats.club_orders?.total_orders || 0)} | 
                                • Stock Value: {formatNumberWithComma2(stats.club_stocks?.total_value || 0)} | 
                                • Inventory Transactions: {formatNumberWithComma2(stats.club_inventory?.total_transactions || 0)} | 
                                • Active Counters: {formatNumberWithComma2(stats.counters?.active_counters || 0)} | 
                                • Stock Adjustments: {formatNumberWithComma2(stats.club_stock_history?.total_adjustments || 0)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
                                Last Updated: {new Date().toLocaleTimeString()}
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="secondary"
                                startIcon={<RefreshIcon />}
                                onClick={fetchDashboardStats}
                                size="small"
                            >
                                Refresh Dashboard
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Container>
    );
};

export default ClubDashboard;